import type { L10n } from "../lib/locales"
import { brandingService } from "./branding"

export type IconName = "presence" | "branding" | "code" | "mobile" | "erp" | "workflow" | "ai" | "education" | "cloud"
export type FAQ = { q: L10n; a: L10n }
export type Service = {
  slug: string
  icon: IconName
  tier: "core" | "supporting"
  title: L10n
  summary: L10n
  heroLead: L10n
  problem: L10n
  approach: L10n
  capabilities: L10n[]
  useCases: L10n[]
  faqs: FAQ[]
  relatedSolutions: string[]
}

// Public copy describes the customer's needs and outcomes, not implementation details.
export const services: Service[] = [
  {
    "slug": "digital-presence",
    "icon": "presence",
    "tier": "core",
    "title": {
      "en": "Websites & Digital Presence",
      "ar": "المواقع والحضور الرقمي"
    },
    "summary": {
      "en": "Websites, stores and campaign pages that explain your business clearly and make it easy for customers to reach you.",
      "ar": "مواقع ومتاجر وصفحات حملات تعرّف بأعمالك بوضوح وتسهّل وصول العملاء إليك."
    },
    "heroLead": {
      "en": "Give customers a clear picture of your business, what you offer and how to take the next step.",
      "ar": "قدّم لعملائك صورة واضحة عن أعمالك وما تقدمه وكيف يتواصلون معك."
    },
    "problem": {
      "en": "An outdated or confusing website can leave visitors unsure about your services, your products or how to contact you.",
      "ar": "قد يجعل الموقع القديم أو غير الواضح الزائر غير متأكد مما تقدمه أو كيف يتواصل معك."
    },
    "approach": {
      "en": "We agree the content and goals with you, design a clear customer journey, then review the pages together before launch.",
      "ar": "نتفق معك على المحتوى والأهداف، ونصمم رحلة واضحة للعميل، ثم نراجع الصفحات معك قبل الإطلاق."
    },
    "capabilities": [
      {
        "en": "Company and service websites",
        "ar": "مواقع الشركات والخدمات"
      },
      {
        "en": "Online stores and product pages",
        "ar": "متاجر إلكترونية وصفحات المنتجات"
      },
      {
        "en": "Campaign and booking pages",
        "ar": "صفحات الحملات والحجز"
      },
      {
        "en": "Arabic and English content",
        "ar": "محتوى بالعربية والإنجليزية"
      },
      {
        "en": "Pages that work well on phones",
        "ar": "صفحات مناسبة للجوال"
      },
      {
        "en": "Content editing and search visibility",
        "ar": "تعديل المحتوى وتحسين الظهور في البحث"
      }
    ],
    "useCases": [
      {
        "en": "A company presenting its services to new customers.",
        "ar": "شركة تعرّف بخدماتها لعملاء جدد."
      },
      {
        "en": "A retailer selling products online.",
        "ar": "متجر يبيع منتجاته عبر الإنترنت."
      },
      {
        "en": "A campaign collecting inquiries or bookings.",
        "ar": "حملة تستقبل الاستفسارات أو الحجوزات."
      }
    ],
    "faqs": [
      {
        "q": {
          "en": "Can I update the content myself?",
          "ar": "هل يمكنني تعديل المحتوى بنفسي؟"
        },
        "a": {
          "en": "Yes. We agree what you need to edit and show your team how to manage it.",
          "ar": "نعم. نحدد ما تحتاج إلى تعديله ونشرح لفريقك طريقة إدارته."
        }
      },
      {
        "q": {
          "en": "Can the website use our existing brand?",
          "ar": "هل يمكن استخدام هويتنا الحالية؟"
        },
        "a": {
          "en": "Yes. We use your approved brand and agree any additional design work before starting.",
          "ar": "نعم. نستخدم هويتك المعتمدة ونتفق على أي أعمال تصميم إضافية قبل البدء."
        }
      }
    ],
    "relatedSolutions": [
      "ecommerce",
      "marketing-agencies"
    ]
  },
  brandingService,
  {
    "slug": "custom-software",
    "icon": "code",
    "tier": "core",
    "title": {
      "en": "Custom Business Applications",
      "ar": "أنظمة وتطبيقات لأعمالك"
    },
    "summary": {
      "en": "Applications and dashboards that organize your work, follow your process and give your team a clear view of progress.",
      "ar": "تطبيقات ولوحات متابعة تنظم عملك وتناسب خطواته وتمنح فريقك رؤية واضحة للتقدم."
    },
    "heroLead": {
      "en": "Turn scattered tasks and spreadsheets into one place where your team can work and follow progress.",
      "ar": "اجمع المهام والجداول المتفرقة في مكان واحد يعمل فيه فريقك ويتابع التقدم."
    },
    "problem": {
      "en": "When work is spread across spreadsheets and messages, tasks can be missed and managers struggle to see what needs attention.",
      "ar": "عندما يتوزع العمل بين الجداول والرسائل قد تضيع المهام ويصعب على الإدارة معرفة ما يحتاج إلى متابعة."
    },
    "approach": {
      "en": "We understand how your team works, agree the most important features, and deliver in stages you can try and review.",
      "ar": "نفهم طريقة عمل فريقك، ونتفق على المزايا الأهم، ثم ننفذ على مراحل يمكنك تجربتها ومراجعتها."
    },
    "capabilities": [
      {
        "en": "Applications tailored to your daily work",
        "ar": "تطبيقات تناسب عملك اليومي"
      },
      {
        "en": "Customer and employee portals",
        "ar": "بوابات للعملاء والموظفين"
      },
      {
        "en": "Task tracking and approvals",
        "ar": "متابعة المهام والموافقات"
      },
      {
        "en": "Reports and management dashboards",
        "ar": "تقارير ولوحات متابعة للإدارة"
      },
      {
        "en": "Connections between the tools you use",
        "ar": "ربط الأدوات التي تستخدمها"
      },
      {
        "en": "Team training and handover",
        "ar": "تدريب الفريق وتسليم المشروع"
      }
    ],
    "useCases": [
      {
        "en": "A team replacing a shared spreadsheet with clear task tracking.",
        "ar": "فريق يستبدل جدولًا مشتركًا بمتابعة واضحة للمهام."
      },
      {
        "en": "Customers checking their requests without calling the team.",
        "ar": "عملاء يتابعون طلباتهم دون الاتصال بالفريق."
      },
      {
        "en": "Managers seeing information from several departments in one place.",
        "ar": "إدارة تتابع معلومات عدة أقسام في مكان واحد."
      }
    ],
    "faqs": [
      {
        "q": {
          "en": "How do you decide what to build first?",
          "ar": "كيف تحددون ما يُنفذ أولًا؟"
        },
        "a": {
          "en": "We prioritize the features that solve your most pressing problems and agree the stages with you.",
          "ar": "نعطي الأولوية للمزايا التي تعالج المشكلات الأهم ونتفق معك على المراحل."
        }
      },
      {
        "q": {
          "en": "Can the application grow with our business?",
          "ar": "هل يمكن تطوير التطبيق مع نمو أعمالنا؟"
        },
        "a": {
          "en": "We plan around your current needs and discuss likely additions so future changes can be considered from the start.",
          "ar": "نخطط حسب احتياجاتك الحالية ونناقش الإضافات المتوقعة لنأخذ التطوير المستقبلي في الاعتبار."
        }
      }
    ],
    "relatedSolutions": [
      "professional-services",
      "real-estate"
    ]
  },
  {
    "slug": "mobile-apps",
    "icon": "mobile",
    "tier": "core",
    "title": {
      "en": "Mobile Applications",
      "ar": "تطبيقات الجوال"
    },
    "summary": {
      "en": "Apps that help customers book, order and follow services, and help employees complete their work from their phones.",
      "ar": "تطبيقات تساعد العملاء على الحجز والطلب والمتابعة، وتساعد الموظفين على إنجاز العمل من هواتفهم."
    },
    "heroLead": {
      "en": "Put the services your customers and employees need within easy reach on their phones.",
      "ar": "اجعل الخدمات التي يحتاجها عملاؤك وموظفوك قريبة منهم على هواتفهم."
    },
    "problem": {
      "en": "Customers need a simple way to book and follow services, while field teams need to send updates without paperwork or repeated calls.",
      "ar": "يحتاج العملاء إلى طريقة سهلة للحجز ومتابعة الخدمات، وتحتاج الفرق الميدانية إلى إرسال التحديثات دون أوراق أو مكالمات متكررة."
    },
    "approach": {
      "en": "We agree the key tasks, design easy-to-use screens and test the experience with you before launch.",
      "ar": "نحدد المهام الأساسية، ونصمم شاشات سهلة الاستخدام، ونختبر التجربة معك قبل الإطلاق."
    },
    "capabilities": [
      {
        "en": "Customer booking and ordering apps",
        "ar": "تطبيقات للحجز والطلب"
      },
      {
        "en": "Employee and field-team apps",
        "ar": "تطبيقات للموظفين والفرق الميدانية"
      },
      {
        "en": "Request and delivery tracking",
        "ar": "متابعة الطلبات والتسليم"
      },
      {
        "en": "Notifications and customer updates",
        "ar": "إشعارات وتحديثات للعملاء"
      },
      {
        "en": "Arabic and English screens",
        "ar": "شاشات بالعربية والإنجليزية"
      },
      {
        "en": "Connection to your existing business tools",
        "ar": "الربط بأدوات العمل الحالية"
      }
    ],
    "useCases": [
      {
        "en": "A service company accepting bookings and tracking visits.",
        "ar": "شركة خدمات تستقبل الحجوزات وتتابع الزيارات."
      },
      {
        "en": "A field team recording completed work from the job site.",
        "ar": "فريق ميداني يسجل العمل المنجز من موقع المهمة."
      },
      {
        "en": "Customers checking order status from their phones.",
        "ar": "عملاء يتابعون حالة طلباتهم من هواتفهم."
      }
    ],
    "faqs": [
      {
        "q": {
          "en": "Can the app work on customers' different phones?",
          "ar": "هل يعمل التطبيق على هواتف العملاء المختلفة؟"
        },
        "a": {
          "en": "We agree the phones and devices to support at the start and test the app for them.",
          "ar": "نتفق على الهواتف والأجهزة المطلوبة في البداية ونختبر التطبيق عليها."
        }
      },
      {
        "q": {
          "en": "Can the app use our current business information?",
          "ar": "هل يمكن ربط التطبيق ببيانات العمل الحالية؟"
        },
        "a": {
          "en": "We review where the information is kept and agree how the app will use and update it.",
          "ar": "نراجع مكان حفظ المعلومات ونتفق على كيفية استخدام التطبيق لها وتحديثها."
        }
      }
    ],
    "relatedSolutions": [
      "ecommerce",
      "real-estate"
    ]
  },
  {
    "slug": "erp-crm",
    "icon": "erp",
    "tier": "core",
    "title": {
      "en": "Business & Customer Management",
      "ar": "إدارة الأعمال والعملاء"
    },
    "summary": {
      "en": "Organize sales, customers, projects and team responsibilities in one place, with reports that help you follow your business.",
      "ar": "نظّم المبيعات والعملاء والمشاريع ومسؤوليات الفريق في مكان واحد، مع تقارير تساعدك على متابعة أعمالك."
    },
    "heroLead": {
      "en": "Give your team a shared view of customers and daily work, from the first inquiry to delivery and payment.",
      "ar": "امنح فريقك رؤية مشتركة للعملاء والعمل اليومي، من أول استفسار حتى التسليم والتحصيل."
    },
    "problem": {
      "en": "Scattered customer details and separate records make follow-up difficult and leave managers without a clear view of progress.",
      "ar": "تشتت بيانات العملاء والسجلات المنفصلة يصعّب المتابعة ويحرم الإدارة من رؤية واضحة للتقدم."
    },
    "approach": {
      "en": "We understand your departments and daily tasks, organize them into a clear way of working, and help your team start using it.",
      "ar": "نفهم أقسامك ومهامك اليومية، وننظمها في طريقة عمل واضحة، ونساعد فريقك على استخدامها."
    },
    "capabilities": [
      {
        "en": "Customer records and sales follow-up",
        "ar": "بيانات العملاء ومتابعة المبيعات"
      },
      {
        "en": "Quotations, invoices and payments",
        "ar": "عروض الأسعار والفواتير والمدفوعات"
      },
      {
        "en": "Project tasks and team responsibilities",
        "ar": "مهام المشاريع ومسؤوليات الفريق"
      },
      {
        "en": "Purchase and leave approvals",
        "ar": "موافقات المشتريات والإجازات"
      },
      {
        "en": "Clear management reports",
        "ar": "تقارير واضحة للإدارة"
      },
      {
        "en": "Moving existing records and training the team",
        "ar": "نقل السجلات الحالية وتدريب الفريق"
      }
    ],
    "useCases": [
      {
        "en": "A sales team following every inquiry and opportunity.",
        "ar": "فريق مبيعات يتابع كل استفسار وفرصة."
      },
      {
        "en": "A service company connecting projects with invoices and payments.",
        "ar": "شركة خدمات تربط المشاريع بالفواتير والمدفوعات."
      },
      {
        "en": "Managers tracking work across departments.",
        "ar": "إدارة تتابع العمل بين الأقسام."
      }
    ],
    "faqs": [
      {
        "q": {
          "en": "Can you improve the way we currently manage work?",
          "ar": "هل يمكن تطوير طريقة إدارة العمل الحالية؟"
        },
        "a": {
          "en": "Yes. We review what you use today and agree the improvements that matter most to your team.",
          "ar": "نعم. نراجع ما تستخدمه حاليًا ونتفق على التحسينات الأكثر أهمية لفريقك."
        }
      },
      {
        "q": {
          "en": "Will you help our team learn to use it?",
          "ar": "هل تساعدون فريقنا على استخدامه؟"
        },
        "a": {
          "en": "We agree a training and handover plan, with clear guidance for the people who will use it.",
          "ar": "نتفق على خطة تدريب وتسليم، مع شرح واضح للأشخاص الذين سيستخدمونه."
        }
      }
    ],
    "relatedSolutions": [
      "marketing-agencies",
      "real-estate",
      "professional-services"
    ]
  },
  {
    "slug": "automation",
    "icon": "workflow",
    "tier": "core",
    "title": {
      "en": "Simplifying Daily Work",
      "ar": "تبسيط العمل اليومي"
    },
    "summary": {
      "en": "Reduce repeated tasks, organize approvals and keep customers informed without following every step by hand.",
      "ar": "قلّل المهام المتكررة، ونظّم الموافقات، وأبقِ العملاء على اطلاع دون متابعة كل خطوة يدويًا."
    },
    "heroLead": {
      "en": "Give your team more time for customers by reducing repeated data entry and follow-up.",
      "ar": "امنح فريقك وقتًا أكبر للعملاء عبر تقليل إدخال البيانات والمتابعة المتكررة."
    },
    "problem": {
      "en": "Teams spend time copying information, chasing approvals and preparing the same reports every week.",
      "ar": "يقضي الفريق وقتًا في نسخ المعلومات وملاحقة الموافقات وإعداد التقارير نفسها كل أسبوع."
    },
    "approach": {
      "en": "We identify repeated tasks with your team, agree which can run automatically and keep important decisions with the right people.",
      "ar": "نحدد مع فريقك المهام المتكررة، ونتفق على ما يمكن إنجازه تلقائيًا، ونُبقي القرارات المهمة لدى المسؤولين عنها."
    },
    "capabilities": [
      {
        "en": "Customer follow-up reminders",
        "ar": "تذكيرات متابعة العملاء"
      },
      {
        "en": "Automatic order and status updates",
        "ar": "تحديث الطلبات وحالتها تلقائيًا"
      },
      {
        "en": "Clear approval steps",
        "ar": "خطوات موافقة واضحة"
      },
      {
        "en": "Scheduled reports",
        "ar": "تقارير مجدولة"
      },
      {
        "en": "Sharing information between departments",
        "ar": "تبادل المعلومات بين الأقسام"
      },
      {
        "en": "Alerts when work needs attention",
        "ar": "تنبيهات عند الحاجة إلى متابعة"
      }
    ],
    "useCases": [
      {
        "en": "A store updating stock when an order arrives.",
        "ar": "متجر يحدث المخزون عند وصول طلب."
      },
      {
        "en": "A sales team receiving reminders to follow up with customers.",
        "ar": "فريق مبيعات تصله تذكيرات بمتابعة العملاء."
      },
      {
        "en": "Managers receiving regular progress reports.",
        "ar": "إدارة تستقبل تقارير دورية عن تقدم العمل."
      }
    ],
    "faqs": [
      {
        "q": {
          "en": "Do we need to change all our current tools?",
          "ar": "هل نحتاج إلى تغيير جميع أدواتنا؟"
        },
        "a": {
          "en": "We review your current tools first and agree what can be connected or improved.",
          "ar": "نراجع أدواتك الحالية أولًا ونتفق على ما يمكن ربطه أو تحسينه."
        }
      },
      {
        "q": {
          "en": "Can important actions still require approval?",
          "ar": "هل يمكن أن تتطلب الإجراءات المهمة موافقة؟"
        },
        "a": {
          "en": "Yes. We agree who approves each important step before it is completed.",
          "ar": "نعم. نحدد من يوافق على كل خطوة مهمة قبل تنفيذها."
        }
      }
    ],
    "relatedSolutions": [
      "marketing-agencies",
      "ecommerce",
      "professional-services"
    ]
  },
  {
    "slug": "ai-solutions",
    "icon": "ai",
    "tier": "core",
    "title": {
      "en": "Smart Business Assistance",
      "ar": "مساعدات ذكية للأعمال"
    },
    "summary": {
      "en": "Help your team find information, summarize documents and prepare replies, with review before important decisions.",
      "ar": "ساعد فريقك على العثور على المعلومات وتلخيص المستندات وتجهيز الردود، مع مراجعة القرارات المهمة."
    },
    "heroLead": {
      "en": "Make everyday information easier to use, so your team can respond and make decisions with less repeated effort.",
      "ar": "اجعل المعلومات اليومية أسهل استخدامًا ليتمكن فريقك من الرد واتخاذ القرارات بجهد متكرر أقل."
    },
    "problem": {
      "en": "Important information is buried in documents and conversations, and staff spend time reading, searching and writing similar replies.",
      "ar": "تختبئ المعلومات المهمة داخل المستندات والمحادثات، ويقضي الموظفون وقتًا في القراءة والبحث وكتابة ردود متشابهة."
    },
    "approach": {
      "en": "We agree a useful task, test the results with your team and define where a person must review or approve the work.",
      "ar": "نحدد مهمة مفيدة، ونختبر النتائج مع فريقك، ونتفق على مواضع المراجعة أو الموافقة البشرية."
    },
    "capabilities": [
      {
        "en": "Finding answers in company documents",
        "ar": "العثور على الإجابات في مستندات الشركة"
      },
      {
        "en": "Summarizing reports and conversations",
        "ar": "تلخيص التقارير والمحادثات"
      },
      {
        "en": "Preparing draft customer replies",
        "ar": "تجهيز مسودات ردود العملاء"
      },
      {
        "en": "Sorting incoming requests",
        "ar": "تصنيف الطلبات الواردة"
      },
      {
        "en": "Extracting useful information from documents",
        "ar": "استخراج المعلومات المفيدة من المستندات"
      },
      {
        "en": "Review and approval before important actions",
        "ar": "مراجعة وموافقة قبل الإجراءات المهمة"
      }
    ],
    "useCases": [
      {
        "en": "A support team preparing replies from approved information.",
        "ar": "فريق دعم يجهز الردود من معلومات معتمدة."
      },
      {
        "en": "Managers reading a short summary of weekly activity.",
        "ar": "إدارة تقرأ ملخصًا قصيرًا لنشاط الأسبوع."
      },
      {
        "en": "Employees finding information without searching many files.",
        "ar": "موظفون يجدون المعلومات دون البحث في ملفات كثيرة."
      }
    ],
    "faqs": [
      {
        "q": {
          "en": "Does this replace our team?",
          "ar": "هل يحل هذا محل فريقنا؟"
        },
        "a": {
          "en": "The goal is to help people with repeated tasks. Your team remains responsible for review and important decisions.",
          "ar": "الهدف مساعدة الأشخاص في المهام المتكررة، ويظل فريقك مسؤولًا عن المراجعة والقرارات المهمة."
        }
      },
      {
        "q": {
          "en": "How do you handle our information?",
          "ar": "كيف تتعاملون مع معلوماتنا؟"
        },
        "a": {
          "en": "We agree what information may be used and who may access it before work begins.",
          "ar": "نتفق على المعلومات المسموح باستخدامها ومن يمكنه الاطلاع عليها قبل بدء العمل."
        }
      }
    ],
    "relatedSolutions": [
      "professional-services",
      "marketing-agencies"
    ]
  },
  {
    "slug": "lms-education",
    "icon": "education",
    "tier": "core",
    "title": {
      "en": "Education & Training Platforms",
      "ar": "منصات التعليم والتدريب"
    },
    "summary": {
      "en": "Bring courses, learners, instructors and progress reports together in an easy-to-manage learning experience.",
      "ar": "اجمع الدورات والمتعلمين والمدرّسين وتقارير التقدم في تجربة تعليم يسهل إدارتها."
    },
    "heroLead": {
      "en": "Help learners reach their lessons and help your team manage enrollment, assessments and progress in one place.",
      "ar": "ساعد المتعلم على الوصول إلى دروسه، وفريقك على إدارة التسجيل والتقييمات والتقدم في مكان واحد."
    },
    "problem": {
      "en": "When lessons, attendance and payments are kept separately, learners need more help and administrators repeat the same work.",
      "ar": "عندما تنفصل الدروس والحضور والمدفوعات يحتاج المتعلم إلى مساعدة أكبر وتكرر الإدارة العمل نفسه."
    },
    "approach": {
      "en": "We understand how you teach and manage courses, design a clear experience for learners and instructors, then review it with your team.",
      "ar": "نفهم طريقة التعليم وإدارة الدورات لديك، ونصمم تجربة واضحة للمتعلمين والمدرّسين، ثم نراجعها مع فريقك."
    },
    "capabilities": [
      {
        "en": "Courses, lessons and learning materials",
        "ar": "الدورات والدروس والمواد التعليمية"
      },
      {
        "en": "Learner registration and instructor accounts",
        "ar": "تسجيل المتعلمين وحسابات المدرّسين"
      },
      {
        "en": "Assessments, assignments and certificates",
        "ar": "التقييمات والواجبات والشهادات"
      },
      {
        "en": "Class schedules and attendance",
        "ar": "جداول الدروس والحضور"
      },
      {
        "en": "Course payments and subscriptions",
        "ar": "مدفوعات الدورات والاشتراكات"
      },
      {
        "en": "Progress and participation reports",
        "ar": "تقارير التقدم والمشاركة"
      }
    ],
    "useCases": [
      {
        "en": "An academy offering online courses.",
        "ar": "أكاديمية تقدم دورات عبر الإنترنت."
      },
      {
        "en": "A training center managing groups and certificates.",
        "ar": "مركز تدريب يدير المجموعات والشهادات."
      },
      {
        "en": "A company organizing employee training.",
        "ar": "شركة تنظم تدريب الموظفين."
      }
    ],
    "faqs": [
      {
        "q": {
          "en": "Can we include administration as well as lessons?",
          "ar": "هل تشمل المنصة الإدارة إلى جانب الدروس؟"
        },
        "a": {
          "en": "Yes. We agree the enrollment, attendance, payment and reporting features you need.",
          "ar": "نعم. نتفق على مزايا التسجيل والحضور والمدفوعات والتقارير التي تحتاجها."
        }
      },
      {
        "q": {
          "en": "Can our current courses and learner records be moved?",
          "ar": "هل يمكن نقل الدورات وبيانات المتعلمين الحالية؟"
        },
        "a": {
          "en": "We review your current materials and records and agree a plan to move the information you need.",
          "ar": "نراجع المواد والسجلات الحالية ونتفق على خطة لنقل المعلومات التي تحتاجها."
        }
      }
    ],
    "relatedSolutions": []
  },
  {
    "slug": "cloud-devops",
    "icon": "cloud",
    "tier": "supporting",
    "title": {
      "en": "Hosting & Maintenance",
      "ar": "الاستضافة والصيانة"
    },
    "summary": {
      "en": "Help keep your website and applications available, maintained and recoverable, with support arrangements suited to your business.",
      "ar": "ساعد موقعك وتطبيقاتك على الاستمرار مع الصيانة واستعادة البيانات، وترتيبات دعم تناسب أعمالك."
    },
    "heroLead": {
      "en": "Keep your digital services ready for daily use, with someone to follow problems and maintain them.",
      "ar": "حافظ على جاهزية خدماتك الرقمية للاستخدام اليومي، مع متابعة المشكلات وصيانتها."
    },
    "problem": {
      "en": "Unexpected outages, missed maintenance and missing copies of important information can interrupt work and leave customers waiting.",
      "ar": "قد يوقف العملَ تعطلٌ مفاجئ أو صيانة مهملة أو غياب نسخ من المعلومات المهمة، ويترك العملاء في الانتظار."
    },
    "approach": {
      "en": "We review what your business relies on, agree a maintenance and recovery plan, and define how issues will be followed up.",
      "ar": "نراجع ما تعتمد عليه أعمالك، ونتفق على خطة للصيانة واستعادة البيانات، ونحدد طريقة متابعة المشكلات."
    },
    "capabilities": [
      {
        "en": "Website and application hosting",
        "ar": "استضافة المواقع والتطبيقات"
      },
      {
        "en": "Regular maintenance and updates",
        "ar": "صيانة وتحديثات دورية"
      },
      {
        "en": "Checking availability and following issues",
        "ar": "متابعة الجاهزية والمشكلات"
      },
      {
        "en": "Copies of important information and recovery planning",
        "ar": "نسخ المعلومات المهمة والتخطيط لاستعادتها"
      },
      {
        "en": "Preparing services for launch",
        "ar": "تجهيز الخدمات للإطلاق"
      },
      {
        "en": "Support arrangements agreed for your needs",
        "ar": "ترتيبات دعم حسب احتياجاتك"
      }
    ],
    "useCases": [
      {
        "en": "A company maintaining its website after launch.",
        "ar": "شركة تصون موقعها بعد الإطلاق."
      },
      {
        "en": "A business preparing to recover important information if needed.",
        "ar": "شركة تستعد لاستعادة المعلومات المهمة عند الحاجة."
      },
      {
        "en": "A team arranging support for daily applications.",
        "ar": "فريق ينظم دعم التطبيقات التي يستخدمها يوميًا."
      }
    ],
    "faqs": [
      {
        "q": {
          "en": "Can you support an existing website or application?",
          "ar": "هل يمكن دعم موقع أو تطبيق قائم؟"
        },
        "a": {
          "en": "We review its current condition and agree the maintenance and support work needed.",
          "ar": "نراجع حالته الحالية ونتفق على أعمال الصيانة والدعم المطلوبة."
        }
      },
      {
        "q": {
          "en": "How is support arranged?",
          "ar": "كيف تُنظم خدمة الدعم؟"
        },
        "a": {
          "en": "We agree what is covered, how issues are reported and the response arrangements before starting.",
          "ar": "نتفق على نطاق الدعم وطريقة الإبلاغ عن المشكلات وترتيبات الاستجابة قبل البدء."
        }
      }
    ],
    "relatedSolutions": [
      "ecommerce"
    ]
  }
]

export const coreServices = services.filter((s) => s.tier === "core")
export const getService = (slug: string) => services.find((s) => s.slug === slug)
